import React, { useState } from 'react'
import { PageLayout } from '../components/common/PageLayout'
import { Section, Button } from '../components/common/DesignComponents'
import { useNavigation } from '../contexts/NavigationContext'

export function CareersPage(): React.ReactElement {
  const { openModal } = useNavigation()
  const [appliedJob, setAppliedJob] = useState<string | null>(null)

  const handleApply = (jobTitle: string) => {
    setAppliedJob(jobTitle)
    console.log('Applied for:', jobTitle)
    // Open contact modal for application submission
    openModal('contact' as any)
    setTimeout(() => setAppliedJob(null), 2000)
  }
  const jobs = [
    {
      title: 'Senior Frontend Developer',
      department: 'Engineering',
      level: 'Senior',
      location: 'Remote',
      description: 'Build magical user experiences with React and TypeScript. Help children learn and grow through engaging gameplay.'
    },
    {
      title: 'Learning Experience Designer',
      department: 'Product',
      level: 'Mid-Level',
      location: 'San Francisco, CA',
      description: 'Design educational content and game mechanics that make learning fun. Work with child psychologists and educators.'
    },
    {
      title: 'Backend Engineer',
      department: 'Engineering',
      level: 'Mid-Level',
      location: 'Remote',
      description: 'Scale our Node.js/TypeScript backend. Build APIs that support millions of children worldwide.'
    },
    {
      title: 'Product Manager',
      department: 'Product',
      level: 'Senior',
      location: 'New York, NY',
      description: 'Shape the future of children\'s education and entertainment. Lead product strategy and vision.'
    },
    {
      title: 'Child Safety Specialist',
      department: 'Trust & Safety',
      level: 'Mid-Level',
      location: 'Remote',
      description: 'Ensure BabyKingdom remains a safe, positive space for children. Monitor content and community.'
    },
    {
      title: 'Marketing Manager',
      department: 'Growth',
      level: 'Mid-Level',
      location: 'Los Angeles, CA',
      description: 'Help families discover BabyKingdom. Create campaigns that inspire learning and growth.'
    },
  ]

  const values = [
    { icon: '🌟', title: 'Child-Focused', description: 'Every decision we make puts children\'s wellbeing first' },
    { icon: '🚀', title: 'Innovation', description: 'We pioneer new ways to make learning engaging and fun' },
    { icon: '🤝', title: 'Collaboration', description: 'We believe diverse teams build better products' },
    { icon: '📚', title: 'Learning', description: 'We invest in continuous growth for our team members' },
  ]

  return (
    <PageLayout title="Careers" subtitle="Join our mission to empower children's growth">
      <Section title="Why Work at BabyKingdom?">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          {values.map((value, idx) => (
            <div key={idx} className="p-6 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl border-4 border-indigo-200 text-center">
              <div className="text-6xl mb-3">{value.icon}</div>
              <h4 className="font-black text-indigo-700 mb-2 text-lg">{value.title}</h4>
              <p className="text-gray-700 font-semibold">{value.description}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Open Positions">
        <div className="space-y-6">
          {jobs.map((job, idx) => (
            <div key={idx} className="p-8 bg-gradient-to-r from-white to-purple-50 rounded-2xl border-4 border-purple-200 hover:shadow-lg transition">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                <div>
                  <h3 className="text-2xl font-black text-purple-700">{job.title}</h3>
                  <div className="flex flex-wrap gap-3 mt-2">
                    <span className="inline-block px-3 py-1 bg-blue-200 text-blue-700 font-bold rounded-full text-sm">
                      {job.department}
                    </span>
                    <span className="inline-block px-3 py-1 bg-pink-200 text-pink-700 font-bold rounded-full text-sm">
                      {job.level}
                    </span>
                    <span className="inline-block px-3 py-1 bg-green-200 text-green-700 font-bold rounded-full text-sm">
                      📍 {job.location}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => handleApply(job.title)}
                  className="px-6 py-2 bg-purple-400 text-white font-black rounded-full hover:bg-purple-500 whitespace-nowrap"
                >
                  {appliedJob === job.title ? '✅ Applied!' : 'Apply Now'}
                </button>
              </div>
              <p className="text-gray-700 font-semibold">{job.description}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Our Culture">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="p-8 bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl border-4 border-cyan-200">
            <h4 className="text-2xl font-black text-cyan-700 mb-4">🎮 Fun & Play</h4>
            <p className="text-gray-700 font-semibold mb-4">
              We believe in having fun while we work. Regular game sessions, team events, and creative collaborations keep our culture vibrant.
            </p>
          </div>
          <div className="p-8 bg-gradient-to-br from-rose-50 to-pink-50 rounded-2xl border-4 border-rose-200">
            <h4 className="text-2xl font-black text-rose-700 mb-4">🌱 Growth Mindset</h4>
            <p className="text-gray-700 font-semibold mb-4">
              We invest in your development. Mentorship programs, learning budgets, and career growth opportunities are priorities.
            </p>
          </div>
          <div className="p-8 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl border-4 border-green-200">
            <h4 className="text-2xl font-black text-green-700 mb-4">⚖️ Work-Life Balance</h4>
            <p className="text-gray-700 font-semibold mb-4">
              Flexible hours, remote options, and generous PTO ensure you have time for what matters most in your life.
            </p>
          </div>
          <div className="p-8 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl border-4 border-yellow-200">
            <h4 className="text-2xl font-black text-yellow-700 mb-4">💼 Benefits</h4>
            <p className="text-gray-700 font-semibold mb-4">
              Competitive salary, health insurance, 401k matching, stock options, and unlimited snacks in the office.
            </p>
          </div>
        </div>
      </Section>

      <Section title="Interested in Joining Us?">
        <div className="text-center p-8 bg-gradient-to-r from-purple-100 to-pink-100 rounded-2xl border-4 border-purple-300">
          <p className="text-gray-800 font-bold text-lg mb-6">
            Don't see the perfect fit? We're always looking for talented individuals! Send us your resume.
          </p>
          <a href="mailto:careers@babykingdom.com" className="inline-block px-8 py-3 bg-purple-500 text-white font-black rounded-full hover:bg-purple-600">
            Send Your Resume
          </a>
        </div>
      </Section>
    </PageLayout>
  )
}
